/**
 * Data layer for `commission-preview`. Thin typed fetch wrappers over
 * /api/v1/commission-preview. Raw `fetch()` is confined to this file per
 * the dash contract (D6).
 *
 * Every request injects `x-tenant-id: t1` per dispatch contract.
 */

export interface CommissionPreviewRow {
	id: string;
	tenant_id: string;
	campaign: string;
	publisher: string;
	rule: string;
	base_amount_cents: number;
	tier_amount_cents: number;
	adjustment_cents: number;
	total_cents: number;
	status: "pending" | "approved" | "rejected";
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCommissionPreviewResponse {
	count: number;
	items: CommissionPreviewRow[];
}

export interface CreateCommissionPreviewBody {
	campaign: string;
	publisher: string;
	base_amount_cents: number;
	tier_amount_cents?: number;
	adjustment_cents?: number;
	rule?: string;
	[key: string]: unknown;
}

export interface CommissionPreviewFilter {
	campaign?: string;
	publisher?: string;
	amount_cents?: number;
}

export interface CommissionPreviewCalc {
	base_cents: number;
	tier_cents: number;
	adjustment_cents: number;
	total_cents: number;
}

const BASE = "/api/v1/commission-preview";
const TENANT_HEADERS = {
	accept: "application/json",
	"x-tenant-id": "t1",
} as const;
const TENANT_HEADERS_JSON = {
	...TENANT_HEADERS,
	"content-type": "application/json",
} as const;

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCommissionPreviewResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: TENANT_HEADERS,
	});
	return parseOrThrow<ListCommissionPreviewResponse>(res);
}

export async function create(
	body: CreateCommissionPreviewBody,
): Promise<CommissionPreviewRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: TENANT_HEADERS_JSON,
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CommissionPreviewRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: TENANT_HEADERS,
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

/**
 * Pure-client commission calculator. Deterministic, no IO.
 * total = base + tier + adjustment.
 */
export function computePreview(
	base_cents: number,
	tier_cents: number,
	adjustment_cents: number,
): CommissionPreviewCalc {
	const b = Number.isFinite(base_cents) ? Math.trunc(base_cents) : 0;
	const t = Number.isFinite(tier_cents) ? Math.trunc(tier_cents) : 0;
	const a = Number.isFinite(adjustment_cents) ? Math.trunc(adjustment_cents) : 0;
	return {
		base_cents: b,
		tier_cents: t,
		adjustment_cents: a,
		total_cents: b + t + a,
	};
}

export function formatMoneyCents(cents: number): string {
	const sign = cents < 0 ? "-" : "";
	const abs = Math.abs(cents);
	const dollars = Math.trunc(abs / 100);
	const rem = abs % 100;
	return `${sign}$${dollars.toLocaleString("en-US")}.${String(rem).padStart(2, "0")}`;
}

export const commissionPreviewQueryKeys = {
	all: ["commission-preview"] as const,
	list: () => [...commissionPreviewQueryKeys.all] as const,
	detail: (id: string) => [...commissionPreviewQueryKeys.all, id] as const,
};
